import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RadioFilterOption, DatepickerType } from './radio-and-datepicker-filter.constants';
import { IFilterParams, IDoesFilterPassParams } from 'ag-grid-community';
import { IColumnRadioDetepickerFilterModel } from '@app/shared/models/ag-filter';
import { MonthViewDatepickerHeaderComponent } from '../../month-view-datepicker-header/month-view-datepicker-header.component';
import { fromEvent, Observable } from 'rxjs';
import { MatDatepickerInputEvent, MatDatepicker } from '@angular/material/datepicker';
import { DatepickerRendererService } from '@app/employees/services/datepicker-renderer.service';
import { AbstractFilterWithSubscriptionComponent } from '@app/shared/classes/ag-filter-with-subscription';
import { map } from 'rxjs/operators';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { AppDateAdapter, APP_DATE_FORMATS } from '@app/shared/classes/app-date-adapter';
import { RadioAndDatepickerFilterDOS } from './radio-and-datepicker-filter-dos.service';
import { changeFilterCallback } from '@app/shared/functions';

@Component({
  selector: 'app-radio-and-datepicker-filter',
  templateUrl: './radio-and-datepicker-filter.component.html',
  styleUrls: ['../ag-filters.scss'],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RadioAndDatepickerFilterComponent extends AbstractFilterWithSubscriptionComponent {
  readonly datepickerHeaderComponent = MonthViewDatepickerHeaderComponent;
  readonly periods = Object.values(RadioFilterOption).filter((value) => value !== RadioFilterOption.Custom);

  readonly startDatepicker: DatepickerType.StartDate;
  readonly endDatepicker: DatepickerType.EndDate;
  readonly isCustomChecked$: Observable<boolean> = this._radioAndDatepickerFilterDOS.isCustomChecked$;

  startDate: Date;
  endDate: Date;

  constructor(
    private _radioAndDatepickerFilterDOS: RadioAndDatepickerFilterDOS,
    private _datepickerRendererService: DatepickerRendererService
  ) {
    super();
  }

  agInit(params: IFilterParams): void {
    super.agInit(params);
  }

  isFilterActive(): boolean {
    return this._radioAndDatepickerFilterDOS.getSelectedPeriodSnapshot() !== RadioFilterOption.All;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const selectedPeriod = this._radioAndDatepickerFilterDOS.getSelectedPeriodSnapshot();
    const nodeDate = new Date(this._valueGetter(params.node));
    const selectedFilterName = Object.keys(RadioFilterOption)[Object.values(RadioFilterOption).indexOf(selectedPeriod)];
    const filterFunctionName = `does${selectedFilterName}FilterPass`;
    return this[filterFunctionName](this._removeTimeFromDate(nodeDate));
  }

  getModel(): IColumnRadioDetepickerFilterModel {
    const selectedPeriod = this._radioAndDatepickerFilterDOS.getSelectedPeriodSnapshot();

    if (selectedPeriod === RadioFilterOption.Custom) {
      return {
        radioValue: RadioFilterOption.Custom,
        datepickerFromValue: this.startDate,
        datepickerToValue: this.endDate
      };
    } else {
      return { radioValue: selectedPeriod };
    }
  }

  setModel(model: IColumnRadioDetepickerFilterModel): void {
    this._radioAndDatepickerFilterDOS.updateSelectedPeriod(model?.radioValue);
    if (model?.radioValue === RadioFilterOption.Custom) {
      this.startDate = model?.datepickerFromValue;
      this.endDate = model?.datepickerToValue;
    }
  }

  onRadioChecked(period: RadioFilterOption): void {
    this._radioAndDatepickerFilterDOS.updateSelectedPeriod(period);
    if (period === RadioFilterOption.Custom) {
      this._radioAndDatepickerFilterDOS.updateIsCustomChecked(true);
    } else {
      this._radioAndDatepickerFilterDOS.updateIsCustomChecked(false);
    }
    changeFilterCallback(this._params);
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.startDate = event.value;
    changeFilterCallback(this._params);
  }

  onEndDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.endDate = event.value;
    changeFilterCallback(this._params);
  }

  openDatepicker(picker: MatDatepicker<Date>, pickerType: DatepickerType): void {
    picker.open();
    this._datepickerRendererService.renderRemoveDateButton();
    this.subscribeToRemoveDateButtonClicks(picker, pickerType);
  }

  isRadioChecked(period: RadioFilterOption): Observable<boolean> {
    return this._radioAndDatepickerFilterDOS.selectedPeriod$.pipe(
      map((selectedPeriod) => {
        return selectedPeriod === period;
      })
    );
  }

  doesAllFilterPass(nodeDate: Date): boolean {
    return true;
  }

  doesCurrentYearToDateFilterPass(nodeDate: Date): boolean {
    const today = this._removeTimeFromDate(new Date());
    return nodeDate.getFullYear() === today.getFullYear() && nodeDate <= today;
  }

  doesPreviousYearFilterPass(nodeDate: Date): boolean {
    const today = this._removeTimeFromDate(new Date());
    return nodeDate.getFullYear() === today.getFullYear() - 1;
  }

  doesLastSixMonthsFilterPass(nodeDate: Date): boolean {
    const firstOfMonth = new Date(new Date().setDate(1));
    const sixMonthsAgo = this._removeTimeFromDate(new Date(firstOfMonth.setMonth(firstOfMonth.getMonth() - 5)));
    return nodeDate >= sixMonthsAgo;
  }

  doesCustomFilterPass(nodeDate: Date): boolean {
    const start = this._removeTimeFromDate(this.startDate);
    const end = this._removeTimeFromDate(this.endDate);

    if (start && end) {
      return nodeDate >= start && nodeDate <= end;
    } else if (start) {
      return nodeDate >= start;
    } else if (end) {
      return nodeDate <= end;
    } else {
      return true;
    }
  }

  subscribeToRemoveDateButtonClicks(picker: MatDatepicker<Date>, pickerType: DatepickerType): void {
    const removeDateButton = document.querySelector('.remove-date-button');

    this.subscription.add(
      fromEvent(removeDateButton, 'click').subscribe(() => {
        switch (pickerType) {
          case DatepickerType.StartDate:
            this.startDate = null;
            break;
          case DatepickerType.EndDate:
            this.endDate = null;
            break;
        }
        picker.select(null);
        changeFilterCallback(this._params);
        picker.close();
      })
    );
  }

  private _removeTimeFromDate(date: Date): Date {
    date?.setHours(0, 0, 0, 0);
    return date;
  }
}
